import emailjs from '@emailjs/browser';
import saraVideo from './assets/Sara Official AD 1 (FINAL CORRECTED)_1_1.mp4';
import React, { useState, useEffect, useRef } from 'react';
import saraLogo from './assets/IMG_3133FE06C6A1-1.jpeg';
import { 
  MessageCircle, Shield, Sparkles, Brain, Globe, Zap, 
  Stars, Phone, Calendar, Building2, Languages, 
  PhoneCall, Clock, Users, Speech, Hash, HeadphonesIcon,
  MessagesSquare, Timer, CheckCircle
} from 'lucide-react';

const Website = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [scrollY, setScrollY] = useState(0);
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const [proximity, setProximity] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [formStatus, setFormStatus] = useState('idle');
  
  const buttonRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const containerRef = useRef(null);
  const capabilitiesRef = useRef(null);
  // Add these near your other useState declarations
  const [isCentered, setIsCentered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // add this with your other states

  // At the top of your file, outside the component:
  const keyframes = `
  @keyframes float {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(3px, -3px); }
  }
  @keyframes orbit {
    from { transform: rotate(0deg) translateX(3px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(3px) rotate(-360deg); }
  }
  @keyframes width {
    0%, 100% { width: 60%; opacity: 0.5; }
    50% { width: 80%; opacity: 0.8; }
  }
  @keyframes gradient {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px 5px rgba(56, 189, 248, 0.3); }
    50% { box-shadow: 0 0 30px 10px rgba(56, 189, 248, 0.5); }
  }
  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 0.8; }
    100% { transform: scale(2); opacity: 0; }
  }
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Safe area utilities */
  .safe-top {
    padding-top: env(safe-area-inset-top);
  }

  .safe-bottom {
    padding-bottom: env(safe-area-inset-bottom);
  }

  .pt-safe {
    padding-top: max(1rem, env(safe-area-inset-top));
  }

  .pb-safe {
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }

  /* Additional mobile optimizations */
  @media (max-width: 768px) {
    html {
      /* Prevent unwanted scrolling */
      height: -webkit-fill-available;
    }
    
    body {
      min-height: -webkit-fill-available;
    }
    
    /* Optimize touch targets */
    button, 
    a {
      min-height: 44px;
      min-width: 44px;
    }

    .overflow-scroll {
      -webkit-overflow-scrolling: touch;
    }
  }

  /* Prevent content shift when modals open */
  .modal-open {
    overflow: hidden;
    position: fixed;
    width: 100%;
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = keyframes;
  document.head.appendChild(style);
}

  // New mouse movement detection with increased range and smoother transitions

 useEffect(() => {
    // Check if mobile on initial load
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    
    // Set up observer for mobile center detection
    if (window.innerWidth <= 768) {
      let timeoutId;
      const observer = new IntersectionObserver(
        ([entry]) => {
          // Clear any existing timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          // Set a small delay before changing state
          timeoutId = setTimeout(() => {
            setIsCentered(entry.isIntersecting);
            if (entry.isIntersecting && !phoneNumber) {
              setPhoneNumber(generatePhoneNumber());
            }
          }, 100);
        },
        {
          root: null,
          rootMargin: '-45% 0px -45% 0px',
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]  
        }
      );

      if (buttonRef.current) {
        observer.observe(buttonRef.current);
      }

      return () => {
        observer.disconnect();
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
   
    // Handle resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) / 4;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) / 4;
        setMousePosition({ x, y });
      }

      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const buttonCenterX = rect.left + rect.width / 2;
        const buttonCenterY = rect.top + rect.height / 2;
        
        const distance = Math.sqrt(
          Math.pow(e.clientX - buttonCenterX, 2) + 
          Math.pow(e.clientY - buttonCenterY, 2)
        );
        
        // Increased trigger distance significantly
        const triggerDistance = 600; // Changed from 300 to 600
        const newProximity = Math.max(0, 1 - (distance / triggerDistance));
        setProximity(newProximity);
        
        // Lowered the threshold so it triggers earlier
        if (newProximity > 0.2 && !phoneNumber) { // Changed from 0.3 to 0.2
          setPhoneNumber(generatePhoneNumber());
        }
      }
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
      [featuresRef, statsRef, capabilitiesRef].forEach(ref => {
        if (ref.current && !animatedSections.has(ref.current.id)) {
          const rect = ref.current.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.8) {
            setAnimatedSections(prev => new Set([...prev, ref.current.id]));
          }
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isFlipped, animatedSections, phoneNumber]);

  // Enhanced phone number generation with area code selection
  const generatePhoneNumber = () => {
    return '(844) 950-2746';
  };

  const flipCta = () => {
    const phone = generatePhoneNumber();
    setPhoneNumber(phone);
    setIsFlipped(true);
    setProximity(0);
    setTimeout(() => {
      setIsFlipped(false);
      setPhoneNumber('');
    }, 5000);
  };
    // Add the new handleSubmit function here
  // Replace your existing handleSubmit function with this:
const handleSubmit = async (e) => {
  e.preventDefault();
  setFormStatus('submitting');

  // Add conversion tracking for Google Ads
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-16890966398/pYYwCMWYvaIaEP7invY-',
      'value': 1.0,
      'currency': 'USD'
    });
  }

  const formData = new FormData(e.target);
  const templateParams = {
    name: formData.get('name'),
    phone: formData.get('phone'),
    email: formData.get('email'),
    business: formData.get('business'),
    timestamp: new Date().toLocaleString()
  };

  try {
    await emailjs.send(
      'service_ytzzdos',
      'template_ui2j9lq',
      templateParams,
      'aA-P_zTamCaJFCJkk'
    );
    
    setFormStatus('success');
    e.target.reset();
    
    setTimeout(() => {
      setFormStatus('idle');
    }, 3000);

  } catch (error) {
    console.error('Error:', error);
    setFormStatus('error');
    setTimeout(() => {
      setFormStatus('idle');
    }, 3000);
  }
};
  return (
   <div ref={containerRef} className="min-h-screen bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 text-gray-800 overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(9)].map((_, i) => (
          <div
            key={i}
            className="absolute w-96 h-96 bg-gradient-to-br from-cyan-200/5 to-blue-200/5 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              transform: `translate(-50%, -50%) scale(${Math.random() + 0.5})`,
              animation: `float ${Math.random() * 30 + 50}s infinite ease-in-out`
            }}
          />
        ))}
      </div>
      {/* Enhanced Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-lg safe-top">
        <div className="pt-safe-top">
          <nav className="max-w-7xl mx-auto px-4 py-3 md:py-5">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 md:gap-3 group">
                <div className="relative">
                  <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-cyan-600 animate-pulse" />
                  <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300" />
                </div>
                <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  ElegyLabs
                </span>
              </div>
              <div className="hidden md:flex gap-8">
                {['Home', 'Features', 'Capabilities', 'Connect'].map((item) => (
                  <a 
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    className="relative group py-2"
                  >
                    <span className="text-gray-600 text-lg font-medium group-hover:text-cyan-600 transition-colors">
                      {item}
                    </span>
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                  </a>
                ))}
              </div>
              {/* Mobile menu button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle menu"
              >
                <div className="space-y-1.5">
                  <div className={`w-5 h-0.5 bg-cyan-600 transition-all duration-300 ${isMobileMenuOpen ? 'transform rotate-45 translate-y-2' : ''}`}></div>
                  <div className={`w-5 h-0.5 bg-cyan-600 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></div>
                  <div className={`w-5 h-0.5 bg-cyan-600 transition-all duration-300 ${isMobileMenuOpen ? 'transform -rotate-45 -translate-y-2' : ''}`}></div>
                </div>
              </button>
            </div>

            {/* Mobile menu with optimized spacing */}
            <div 
              className={`md:hidden transition-all duration-300 ease-in-out 
                ${isMobileMenuOpen ? 'max-h-64 opacity-100 mt-2' : 'max-h-0 opacity-0'} 
                overflow-hidden`}
            >
              <div className="py-2 space-y-1">
                {['Home', 'Features', 'Capabilities', 'Connect'].map((item) => (
                  <a 
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-2 px-4 text-gray-600 hover:text-cyan-600 hover:bg-gray-50 rounded-lg transition-colors text-base"
                  >
                    {item}
                  </a>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 pt-12">
        {/* Hero Section with enhanced animations */}
        <div className="relative mb-24">
          <div className="absolute inset-0 flex justify-center">
            <div className="w-full h-full max-w-3xl bg-gradient-to-r from-cyan-300/15 via-blue-300/15 to-cyan-300/15 blur-2xl" />
          </div>
          
          <h1 
            className="text-7xl font-bold text-center mb-6 pb-3 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent relative z-10"
            style={{
              transform: `translate3d(${mousePosition.x * -1}px, ${mousePosition.y * -1}px, 0)`,
              transition: 'transform 0.1s ease-out',
              backgroundSize: '200% auto',
              lineHeight: '1.2'  // Added this
            }}
            >
              Meet Sara, Your
              <br />
              <span className="inline-block pb-2">AI Phone Agent</span>
          </h1>
          {/* Quick capabilities showcase */}
          <div className="flex justify-center gap-8 mb-8">
            {[
              { Icon: Phone, text: "Voice Calls" },
              { Icon: Languages, text: "Multilingual" },
              { Icon: Calendar, text: "Scheduling" },
              { Icon: Building2, text: "Business Ready" }
            ].map(({ Icon, text }, index) => (
              <div key={text} 
                className="flex flex-col items-center gap-2 text-cyan-600"
                style={{ 
                  animation: `fadeIn 0.5s ease-out forwards`,
                  animationDelay: `${index * 0.1}s`,
                  opacity: 0 
                }}
              >
                <Icon className="w-8 h-8" />
                <span className="text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>
          
          <div 
            className="bg-white shadow-2xl rounded-2xl px-8 pt-8 pb-10 max-w-2xl mx-auto mb-12 hover:scale-105 transition-all duration-500 hover:shadow-cyan-100"
            style={{
              transform: `translate3d(${mousePosition.x * 10}px, ${mousePosition.y * 10}px, 0)`,
              transition: 'all 0.1s ease-out'
            }}
          >
            <p className="text-xl text-center leading-relaxed text-gray-600">
              Experience the future of customer service with Sara, your 24/7 AI agent. 
              With natural conversation abilities, multilingual support, and seamless call handling,
              Sara adapts to your business needs while maintaining a human touch.
              <span className="block mt-4 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent font-semibold text-2xl">
                Talk to Sara yourself below ✨
              </span>
            </p>
          </div>
        </div>
        {/* Sara Video Ad */}
        <div 
          className="w-full max-w-3xl mx-auto my-16 opacity-0 transition-all duration-1000"
          style={{
            transform: 'translateY(20px)',
            animation: 'fadeInUp 1s ease forwards'
          }}
        >
          <div className="relative rounded-2xl overflow-hidden shadow-[0_0_25px_-5px_rgba(0,0,0,0.1)] hover:shadow-cyan-100 transition-all duration-500">
            <video 
              className="w-full rounded-2xl hover:scale-[1.02] transition-transform duration-500"
              controls
              autoPlay
              muted
              playsInline
            >
              <source src={saraVideo} type="video/mp4" />
            </video>
          </div>
        </div>
        
        {/* Then the Sara Logo CTA section continues below */}

        {/* Sara Logo CTA */}
        <div className="w-full max-w-2xl mx-auto my-16 flex justify-center items-center">
          <div 
            className="cursor-pointer group relative"
            style={{
              transform: `scale(${1 + proximity * 0.05})`,
              transition: 'transform 0.3s ease-out'
            }}
          >
            {/* Pulsing rings - only visible on hover */}
            <div className="absolute inset-0 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 rounded-full border-2 border-cyan-300/30 animate-[pulse-ring_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
              <div className="absolute inset-0 rounded-full border-2 border-cyan-300/20 animate-[pulse-ring_2s_cubic-bezier(0.4,0,0.6,1)_infinite_500ms]" />
              <div className="absolute inset-0 rounded-full border-2 border-cyan-300/10 animate-[pulse-ring_2s_cubic-bezier(0.4,0,0.6,1)_infinite_1000ms]" />
            </div>
        
            <div 
              ref={buttonRef}
              className={`relative w-72 h-72 transform transition-all duration-500 
                          overflow-hidden rounded-full bg-white
                          ${!isMobile && 'hover:animate-[pulse-glow_2s_ease-in-out_infinite]'}
                          ${proximity > 0.2 && !isMobile ? 'ring-4 ring-cyan-200' : ''}
                          ${isCentered && isMobile ? 'ring-4 ring-cyan-200 scale-105' : ''}
                          `}
            >
              <img 
                src={saraLogo}
                alt="Sara AI Logo"
                className="w-full h-full object-cover scale-150"
              />
              
              {(proximity > 0.2 || (isMobile && isCentered)) && (
                <div 
                  className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden"
                  style={{
                    opacity: isMobile ? (isCentered ? 1 : 0) : (proximity > 0.2 ? 1 : 0),
                    transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isMobile && isCentered ? 'scale(1)' : 'scale(0.95)'
                  }}
                >
                  {/* Animated gradient background with subtle pulse */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-white/95 backdrop-blur-md rounded-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/30 to-blue-50/30 animate-[pulse_3s_ease-in-out_infinite] rounded-full" />
                  </div>
              
                  {/* Floating particles */}
                  <div className="absolute inset-0">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 rounded-full bg-cyan-300/30"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animation: `float ${Math.random() * 3 + 4}s ease-in-out infinite`,
                          animationDelay: `${Math.random() * 2}s`
                        }}
                      />
                    ))}
                  </div>
              
                  {/* Main content container with hover effect */}
                  <div className="relative z-10 transform -translate-y-2 w-full h-full transition-transform duration-300 hover:scale-102">
                    {/* Animated top line */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-300 to-transparent animate-[width_3s_ease-in-out_infinite]" />
                  
                    {/* Main content */}
                    <div className="space-y-3 text-center w-full h-full flex flex-col items-center justify-center">
                      <div className="text-lg font-medium tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 hover:scale-105 transition-transform">
                        Talk with Sara
                      </div>
                      
                      <div className="relative w-full">
                        <div className="relative px-4">
                          <div className="relative">
                            {isMobile ? (
                              <a
                                href={`tel:${phoneNumber?.replace(/[^0-9]/g, '') || generatePhoneNumber().replace(/[^0-9]/g, '')}`}
                                className="block text-2xl md:text-3xl font-bold tracking-tight text-transparent 
                                          bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 
                                          animate-[gradient_3s_ease-in-out_infinite] whitespace-nowrap
                                          active:scale-95 transition-transform cursor-pointer"
                                role="button"
                                aria-label="Call Sara"
                              >
                                {phoneNumber || generatePhoneNumber()}
                                <span className="mt-2 block text-sm font-medium text-cyan-600">
                                  Tap to call
                                </span>
                              </a>
                            ) : (
                              <div className="text-2xl md:text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 animate-[gradient_3s_ease-in-out_infinite] whitespace-nowrap">
                                {phoneNumber || generatePhoneNumber()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    
                      <div className="text-sm font-medium text-cyan-600/80 max-w-[200px] mx-auto">
                        Talk to Sara directly and discover her capabilities firsthand
                      </div>
                    
                      <div className="flex items-center justify-center gap-2 text-cyan-600/90 hover:scale-105 transition-transform">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium tracking-wide">Available 24/7</span>
                      </div>
                    </div>
              
                    {/* Animated bottom line */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent animate-[width_3s_ease-in-out_infinite]" />
                  </div>
              
                  {/* Animated corner accents */}
                  <div className="absolute top-4 right-4 w-12 h-12 animate-[spin_20s_linear_infinite]">
                    <div className="absolute top-0 right-0 w-full h-0.5 bg-gradient-to-l from-cyan-200 to-transparent" />
                    <div className="absolute top-0 right-0 h-full w-0.5 bg-gradient-to-b from-cyan-200 to-transparent" />
                  </div>
                  <div className="absolute bottom-4 left-4 w-12 h-12 animate-[spin_20s_linear_infinite_reverse]">
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-200 to-transparent" />
                    <div className="absolute bottom-0 left-0 h-full w-0.5 bg-gradient-to-t from-cyan-200 to-transparent" />
                  </div>
              
                  {/* Animated dot pattern */}
                  <div className="absolute inset-0 opacity-10">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-1 h-1 rounded-full"
                        style={{
                          background: i % 2 ? 'rgb(34, 211, 238)' : 'rgb(96, 165, 250)',
                          top: i < 2 ? '1.5rem' : 'auto',
                          bottom: i >= 2 ? '1.5rem' : 'auto',
                          left: i % 2 === 0 ? '1.5rem' : 'auto',
                          right: i % 2 === 1 ? '1.5rem' : 'auto',
                          animation: `pulse ${2 + i * 0.5}s ease-in-out infinite`
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Capabilities Section */}
        <div 
          id="capabilities" 
          ref={capabilitiesRef}
          className="mb-24"
        >
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Sara's Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Phone className="w-8 h-8" />,
                title: "Natural Voice Calls",
                desc: "Engages in natural phone conversations with human-like understanding and seamless context awareness"
              },
              {
                icon: <Speech className="w-8 h-8" />,
                title: "Multilingual Mastery",
                desc: "Fluently communicates in multiple languages, ensuring clear and accurate global communication"
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Advanced Call Management",
                desc: "Expertly handles transfers, scheduling, and complex customer service scenarios"
              },
              {
                icon: <Calendar className="w-8 h-8" />,
                title: "Smart Scheduling",
                desc: "Efficiently manages appointments and coordinates complex scheduling requirements"
              }
            ].map((capability, index) => (
              <div
                key={capability.title}
                className={`bg-white p-6 rounded-xl shadow-lg transform transition-all duration-1000
                          hover:shadow-xl hover:scale-105 hover:bg-gradient-to-br hover:from-white hover:to-cyan-50
                          ${animatedSections.has('capabilities') ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg text-cyan-600">
                    {capability.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-cyan-600">{capability.title}</h3>
                    <p className="text-gray-600">{capability.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Enhanced Features Grid */}
        <div 
          id="features" 
          ref={featuresRef} 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24"
        >
          {[
            {
              Icon: Brain,
              title: "Advanced AI Understanding",
              desc: "Natural conversations powered by state-of-the-art language processing, ensuring human-like comprehension and contextual awareness 🧠"
            },
            {
              Icon: Shield,
              title: "Enterprise Security",
              desc: "Bank-grade encryption and security measures to protect every conversation and piece of data 🛡️"
            },
            {
              Icon: Globe,
              title: "Multilingual Excellence",
              desc: "Seamless communication across languages with cultural awareness and natural fluency 🌎"
            }
          ].map((feature, index) => (
            <div 
              key={feature.title}
              className={`group bg-white shadow-lg rounded-xl p-8 text-center transform transition-all duration-1000
                        hover:shadow-xl hover:scale-105 hover:shadow-cyan-100/50
                        ${animatedSections.has('features') ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="flex justify-center mb-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full animate-pulse" />
                <feature.Icon 
                  className="w-16 h-16 text-cyan-600 relative z-10 transform group-hover:rotate-12 transition-transform" 
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                {feature.title}
              </h3>
              <p className="text-lg text-gray-600">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Enhanced Stats Section */}
        <div 
          id="stats" 
          ref={statsRef} 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24"
        >
          {[
            { 
              Icon: Zap, 
              value: "1.2s", 
              label: "Average Response Time",
              detail: "Lightning-fast responses to keep conversations flowing naturally"
            },
            { 
              Icon: Stars, 
              value: "99.9%", 
              label: "Uptime",
              detail: "Reliable availability for round-the-clock customer support"
            },
            { 
              Icon: Brain, 
              value: "1M+", 
              label: "Conversations",
              detail: "Growing smarter with every interaction"
            }
          ].map((stat, index) => (
            <div
              key={stat.label}
              className={`group bg-white shadow-lg rounded-xl p-8 text-center transform transition-all duration-1000
                        hover:shadow-xl hover:scale-105 hover:shadow-cyan-100/50
                        ${animatedSections.has('stats') ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="relative mb-4">
                <stat.Icon 
                  className="absolute -top-4 -right-4 w-12 h-12 text-cyan-600/50 animate-bounce" 
                  style={{ animationDelay: `${index * 150}ms` }} 
                />
                <div className="text-5xl font-bold text-cyan-600 group-hover:scale-110 transition-transform">
                  {stat.value}
                </div>
                <p className="text-lg mt-2 text-gray-600">{stat.label}</p>
                <p className="text-sm mt-1 text-gray-500">{stat.detail}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Beta Sign-up Form */}
        <div className="max-w-4xl mx-auto mb-24 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden group hover:shadow-cyan-100/50 transition-all duration-500">
            {/* Background accent with animation */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/30 via-transparent to-blue-50/30 opacity-50 group-hover:opacity-100 transition-all duration-500" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(56,189,248,0.1),transparent_70%)]" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Join Sara's Beta Program
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 relative group">
                    <label className="block text-sm font-medium text-gray-700 group-hover:text-cyan-600 transition-colors">Name</label>
                    <input 
                      type="text" 
                      name="name"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/30 focus:bg-white 
                                focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:border-cyan-200
                                transition-all duration-300"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2 relative group">
                    <label className="block text-sm font-medium text-gray-700 group-hover:text-cyan-600 transition-colors">Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/30 focus:bg-white 
                                focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:border-cyan-200
                                transition-all duration-300"
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2 relative group">
                  <label className="block text-sm font-medium text-gray-700 group-hover:text-cyan-600 transition-colors">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/30 focus:bg-white 
                              focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:border-cyan-200
                              transition-all duration-300"
                    placeholder="you@company.com"
                    required
                  />
                </div>
                
                <div className="space-y-2 relative group">
                  <label className="block text-sm font-medium text-gray-700 group-hover:text-cyan-600 transition-colors">Business Description</label>
                  <textarea 
                    name="business"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50/30 focus:bg-white 
                              focus:ring-2 focus:ring-cyan-500 focus:border-transparent hover:border-cyan-200
                              transition-all duration-300 h-32 resize-none"
                    placeholder="Tell us about your business and how Sara can help..."
                    required
                  />
                </div>
                
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold
                            hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl 
                            hover:shadow-cyan-100/50 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : 'Request Beta Access'}
                </button>
              
                {/* Success Message */}
                {formStatus === 'success' && (
                  <div className="fixed top-4 right-4 bg-white rounded-lg shadow-xl p-4 animate-slide-in z-50">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span>Thanks for signing up! We'll be in touch soon.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="w-full pb-8 mt-16">
          <div className="border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-600" />
                <span className="text-gray-600 font-medium">ElegyLabs</span>
              </div>
              
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-sm text-gray-500">
                <span>© 2025 ElegyLabs. All rights reserved.</span>
                <div className="flex gap-8">
                  <a href="#" className="hover:text-cyan-600 transition-colors">Privacy Policy</a>
                  <a href="#" className="hover:text-cyan-600 transition-colors">Terms of Service</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        </main>
        </div>
  );
};
export default Website;
